import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryPresets } from '@/lib/query-config';

export interface PublishedReview {
  id: number;
  rating: number;
  comment: string;
  anonymous: boolean;
  practitionerId: string | null;
  practitionerName: string | null;
  practitionerSlug: string | null;
}

/**
 * Public homepage testimonials — reads real published client reviews.
 * Client identities are never exposed: reviews are attributed to a
 * "Verified Client" label, only the practitioner is named.
 */
export function usePublishedReviews(limit = 4, minLength = 40) {
  return useQuery({
    queryKey: ['published-reviews', limit, minLength],
    ...queryPresets.semiStatic,
    queryFn: async (): Promise<PublishedReview[]> => {
      const { data: reviews, error } = await supabase
        .from('reviews')
        .select('id, rating, comment, anonymous, practitioner_id, created_at')
        .eq('is_published', true)
        .not('comment', 'is', null)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      const filtered = (reviews || [])
        .filter((r) => (r.comment || '').trim().length >= minLength)
        .slice(0, limit);

      if (!filtered.length) return [];

      const ids = Array.from(
        new Set(filtered.map((r) => r.practitioner_id).filter(Boolean))
      ) as string[];

      let nameMap = new Map<string, { name: string; slug: string | null }>();
      if (ids.length) {
        const { data: practitioners } = await supabase
          .from('practitioners')
          .select('id, first_name, last_name, slug')
          .in('id', ids);
        nameMap = new Map(
          (practitioners || []).map((p: any) => [
            p.id,
            {
              name: [p.first_name, p.last_name].filter(Boolean).join(' ').trim(),
              slug: p.slug || null,
            },
          ])
        );
      }

      return filtered.map((r) => {
        const p = r.practitioner_id ? nameMap.get(r.practitioner_id) : undefined;
        return {
          id: r.id,
          rating: r.rating ?? 5,
          comment: (r.comment || '').trim(),
          anonymous: r.anonymous ?? true,
          practitionerId: r.practitioner_id ?? null,
          practitionerName: p?.name || null,
          practitionerSlug: p?.slug || null,
        };
      });
    },
  });
}
